<template>
    <div>
        <div class="flex items-center justify-between flex-wrap py-4 gap-x-2">
            <div class="sm:flex-auto">
                <h1 class="text-xl font-semibold text-gray-900 select-none">{{ searchParams.archived_filter ? "Archived" : "" }} Tickets</h1>
            </div>

            <div class="flex items-center justify-start gap-x-2">
                <div class="flex items-center">
                    <secondary-button @click.prevent="showTicketCreateForm">
                        New Ticket
                    </secondary-button>
                </div>

                <simple-dropdown label="Options">
                    <template slot="buttonOptions">
                        <simple-dropdown-option
                            value="Show Archived Tickets"
                            key="archived_tickets"
                            @click="toggleShowArchivedTickets">
                            {{ searchParams.archived_filter ? "Show Current Tickets" : "Show Archived Tickets" }}
                        </simple-dropdown-option>

                        <simple-dropdown-option
                            value="Export Tickets"
                            key="export_tickets"
                            @click="exportTickets">
                            Export Tickets
                        </simple-dropdown-option>

                        <div class="border-b border-gray-200 my-2"></div>

                        <simple-dropdown-option
                            value="Quotes & Invoices"
                            key="quotes_invoices"
                            @click="openLink('/quotes-invoices', false)">
                            Quotes & Invoices
                        </simple-dropdown-option>

                        <div v-if="userCan('manage-ticket-settings')" class="border-b border-gray-200 my-2"></div>
                        <simple-dropdown-option
                            value="Settings"
                            key="ticket_settings"
                            v-if="userCan('manage-ticket-settings')"
                            @click="openLink('/settings/tickets', true)">
                            Maintenance Settings
                        </simple-dropdown-option>
                    </template>
                </simple-dropdown>
            </div>
        </div>

        <expandable-filters-container
            :showExpandOption="true"
            filter-button-text="More Filters"
            @apply-filters="fetchTickets()"
            @reset-filters="clearFilters"
            class="md:mt-0">
            <div slot="top-panel-left">
                <div class="mt-4 sm:mt-0 flex justify-start items-center flex-wrap gap-2 gap-y-4">
                    <div class="btn-group">
                        <simple-dropdown :label="userDropdownLabel ? userDropdownLabel.name : 'All Users'"
                                         v-model="searchParams.user_filter" origin="bottom-start"
                                         @input="fetchTickets">
                            <template slot="buttonOptions">
                                <simple-dropdown-option
                                    :key="null"
                                    :value="undefined">
                                    All Users
                                </simple-dropdown-option>
                                <simple-dropdown-option
                                    v-for="user in users"
                                    :key="user.id"
                                    :value="user.id">
                                    {{ user.name | titlecase }}
                                </simple-dropdown-option>
                            </template>
                        </simple-dropdown>
                    </div>

                    <simple-dropdown :label="statusFilterLabel"
                                     v-model="searchParams.status_filter"
                                     @input="fetchTickets"
                                     origin="bottom-end">
                        <template slot="buttonOptions">
                            <simple-dropdown-option value="open">Open Tickets</simple-dropdown-option>
                            <simple-dropdown-option value="closed">Closed Tickets</simple-dropdown-option>
                            <simple-dropdown-option :value="undefined">All Tickets</simple-dropdown-option>
                        </template>
                    </simple-dropdown>

                    <simple-text-search-input
                        inputStyles="min-width=150px"
                        text-search-placeholder="eg. 100 Example Road"
                        text-search-label="Search"
                        v-model="searchParams.filter_text"
                        @input="fetchTickets"
                    ></simple-text-search-input>

                    <simple-text-search-input
                        text-search-placeholder="eg. TR0024"
                        text-search-label="Ticket Ref."
                        v-model="searchParams.filter_reference"
                        @input="fetchTickets"
                        inputStyles="max-width: 90px">
                    </simple-text-search-input>

                    <span v-if="activeFiltersCount > 0"
                          class="h-6 ml-4 inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-indigo-500 text-white">
                        <span>{{ activeFiltersCount + " Active Filter" | pluralizeOnlyS(activeFiltersCount) }} </span>

                        <button @click.prevent="clearFilters(); fetchTickets();" title="Clear All Filters"
                                class="flex ml-2 cursor-pointer">
                            <icon-x-circle class="w-3 h-3 text-white hover:text-indigo-800 transition duration-300"></icon-x-circle>
                        </button>
                    </span>
                </div>
            </div>

            <div slot="expanded-filters-container" class="bg-white flex justify-around p-8">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <!-- We hide this on mobile because the el-date-picker does not work on mobile -->
                    <div class="hidden md:block col-span-1">
                        <label class="block font-medium">Logged at Date</label>
                        <el-date-picker
                            v-model="searchParams.logged_at_date_range_filter"
                            :picker-options="datePickerOptions"
                            value-format="yyyy-MM-dd"
                            type="daterange"
                            range-separator="-"
                            start-placeholder="From date"
                            end-placeholder="To date">
                        </el-date-picker>
                    </div>

                    <div class="col-span-3 lg:col-span-1">
                        <label class="block font-medium">Select Phase(s)</label>
                        <el-select
                            class="w-100 truncate cursor-pointer"
                            v-model="searchParams.phases_filter"
                            multiple
                            collapse-tags>
                            <el-option
                                v-for="(phase, index) in phases"
                                :key="index"
                                :label="phase.phase"
                                :value="phase.id"
                            ></el-option>
                        </el-select>
                    </div>

                    <div class="col-span-3 lg:col-span-1">
                        <label class="block font-medium">Select Priorities</label>
                        <el-select
                            class="w-100 cursor-pointer"
                            v-model="searchParams.priorities_filter"
                            multiple
                            collapse-tags>
                            <el-option
                                v-for="(priority, index) in priorityOptions"
                                :key="index"
                                :label="priority | titlecase"
                                :value="priority"
                            ></el-option>
                        </el-select>
                    </div>

                    <div class="col-span-3 lg:col-span-1">
                        <label class="block font-medium">Select Service Provider Type(s)</label>
                        <el-select
                            filterable
                            class="w-100"
                            @focus="loadServiceProviderTypes"
                            v-model="searchParams.service_provider_types"
                            multiple
                            collapse-tags>
                            <el-option
                                key="99999"
                                label="No Service Provider"
                                value="No Service Provider"
                            ></el-option>
                            <el-option
                                v-for="(category, index) in serviceProviderTypes"
                                :key="index"
                                :label="category"
                                :value="category"
                            ></el-option>
                        </el-select>
                    </div>

                    <div class="col-span-3 lg:col-span-1" v-if="hasFeature('feat_leases')">
                        <label class="block font-medium">Latest Billing Request Status</label>
                        <el-select
                            class="w-100"
                            v-model="searchParams.transaction_request_statuses"
                            multiple
                            collapse-tags>
                            <el-option
                                v-for="(transactionRequestStatus, index) in transactionRequestStatusOptions"
                                :key="index"
                                :label="transactionRequestStatus | titlecase"
                                :value="transactionRequestStatus"
                            ></el-option>
                        </el-select>
                    </div>

                    <div class="col-span-3 lg:col-span-1">
                        <label class="block font-medium">Resolution Filter</label>
                        <el-select
                            class="w-100"
                            v-model="searchParams.resolution_filter"
                            clearable>
                            <el-option key="resolved" label="Resolved" value="resolved"></el-option>
                            <el-option key="unresolved" label="Unresolved" value="unresolved"></el-option>
                        </el-select>
                    </div>
                </div>
            </div>
        </expandable-filters-container>

        <div class="mt-4 overflow-hidden shadow sm:mx-0 rounded-lg">

            <div v-if="ticketListLoading">
                <div class="flex justify-center items-center w-full p-20">
                    <div class="flex flex-col items-center">
                        <h2 class="text-xl font-normal text-gray-400 my-4 select-none flex align-items-center">
                            <icon-refresh class="h-8 w-8 text-indigo-600 animate-spin mr-2"/>
                            Loading...
                        </h2>
                    </div>
                </div>
            </div>

            <div v-else>
                <div v-if="mutableTicketList.length <= 0" class="flex justify-center items-center w-full p-20">
                    <div class="flex flex-col items-center">
                        <h2 class="text-xl font-normal text-gray-400 my-4 select-none">No matching tickets found</h2>
                    </div>
                </div>

                <!-- Full Width Table-->
                <table v-else class="min-w-full divide-y divide-gray-300">
                    <thead class="bg-white">
                        <tr class="select-none">
                            <!--Status (Lock open or closed) & Priority (color of the opened lock) Column-->
                            <th scope="col" class="py-3.5 text-left text-center font-medium text-gray-900 px-2 hidden lg:table-cell"></th>

                            <th scope="col" class="py-3.5 pl-6 pr-3 text-left text-sm font-medium text-gray-900">
                                <span>Asset</span>
                                <span class="hidden lg:inline-block text-indigo-600">/ Tenant</span>
                                <span class="inline-block lg:hidden">& Ticket Details</span>
                            </th>

                            <th scope="col" class="hidden px-3 py-3.5 text-left text-sm font-medium text-black lg:table-cell">
                                <div class="flex align-items-center">
                                    <sortable-column-header
                                        class="-ml-2 flex align-items-center"
                                        v-model="searchParams.sort.logged_at"
                                        field-name="logged_at"
                                        :sort-direction="searchParams.sort.logged_at"
                                        icon-colour="text-black"
                                        :disable-default="false"
                                        @change="updateSorting">
                                        <template slot="right">
                                            <span class="text-gray-900">Logged &nbsp;/</span>
                                        </template>
                                    </sortable-column-header>
                                    <badge class="text-yellow-600 bg-yellow-100 ml-1" text-size="text-sm">Phase</badge>
                                </div>
                            </th>

                            <th scope="col" class="hidden px-3 py-3.5 text-left text-sm font-medium text-black lg:table-cell">
                                <div class="flex align-items-center">
                                    <span>Service Provider</span>
                                    <span class="text-gray-400">&nbsp;/ Description</span>
                                    <span class="text-yellow-600">&nbsp;/ Last Activity</span>
                                </div>
                            </th>

                            <th scope="col" class="hidden px-3 py-3.5 text-left text-sm font-medium text-gray-900 lg:table-cell whitespace-nowrap">
                                <span class="inline text-indigo-600">Assigned To</span>
                                <sortable-column-header class="-ml-2 flex align-items-center inline text-indigo-600 -ml-5"
                                                        v-model="searchParams.sort.reminder_date"
                                                        field-name="reminder_date"
                                                        :sort-direction="searchParams.sort.reminder_date"
                                                        icon-colour="text-black"
                                                        :disable-default="false"
                                                        @change="updateSorting">
                                    <div class="flex-nowrap" slot="right">
                                        <span class="text-gray-900 ml-0.5">Next Reminder</span>
                                    </div>
                                </sortable-column-header>

                            </th>

                            <th class="hidden sm:table-cell" scope="col"></th>
                        </tr>
                    </thead>

                    <tbody class="divide-y-2 md:divide-y divide-gray-300 md:divide-gray-200 bg-white">
                        <tr v-for="(ticket, index) in mutableTicketList"
                            class="select-none"
                            :class="index % 2 === 1 ? undefined : 'bg-gray-50'">
                            <!--Priority & Status Icon Column-->
                            <td class="hidden lg:table-cell text-center pl-2 pr-1" :title="`${upperFirst(ticket.status)} ticket | ${ticket.priority} priority`">
                                <div class="hidden lg:block rounded font-medium text-sm -mt-1" :class="getTicketReferenceColorStyles(ticket, 'text')">
                                    {{ ticket.ticket_reference_number }}
                                </div>

                                <div v-if="ticket.status ==='open'" class="flex items-center justify-center">
                                    <span class="w-2 h-2 rounded-full"
                                          :class="getTicketReferenceColorStyles(ticket, 'bg')">
                                    </span>
                                </div>

                                <badge v-else text-size="text-2xs" class="-mb-1.5">
                                    <span class="bg-gray-100 text-gray-400 px-1 py-0.5">closed</span>
                                </badge>
                            </td>

                            <!-- MOBILE STYLING -->
                            <td class="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-xl sm:pl-6">
                                <div class="text-lg sm:text-sm">
                                    <div>
                                        <div class="block lg:hidden cursor-pointer" @click.prevent="openLink(`/tickets/${ticket.id}`, false)">
                                            <div class="flex items-center inline">
                                                <span class="w-3 h-3 rounded-full mr-2 inline"
                                                      :class="getTicketReferenceColorStyles(ticket, 'bg')">
                                                </span>

                                                <span>{{ ticket.asset_name }}</span>
                                            </div>

                                            <div class="inline lg:hidden text-gray-400 font-normal">Logged {{ ticket.logged_at_relative }}</div>
                                        </div>

                                        <div class="border-b border-gray-200 md:hidden my-2 md:my-0"></div>

                                        <div class="hidden lg:block cursor-pointer"
                                             @click.prevent="openLink(`/assets/${ticket.asset_id}`, true)"><span>{{ ticket.asset_name }}</span>
                                        </div>

                                        <div v-if="ticket && ticket.primary_tenant" class="block mt-1 text-indigo-600"
                                             :title="getContactDetailString(ticket.primary_tenant, 'tenant')">
                                            {{ ticket.primary_tenant.full_name }}
                                        </div>

                                        <div v-else class="block mt-1 text-gray-400 font-normal">No Primary Tenant</div>
                                    </div>
                                </div>

                                <dl class="font-normal lg:hidden text-lg sm:text-sm cursor-pointer" @click.prevent="showTicketSummary(ticket)">
                                    <dd class="mt-1">
                                        <badge class="text-yellow-600 bg-yellow-100" text-size="text-sm" v-if="ticket.phase">{{ ticket.phase.name }}</badge>
                                    </dd>

                                    <dd class="mt-1 text-gray-400 text-md sm:text-sm cursor-pointer" @click.prevent="showTicketSummary(ticket)">
                                        <div class="line-clamp-1">{{ ticket.description }}</div>

                                        <badge class="text-red-500 bg-red-100" text-size="text-sm" title="Please contact support for assistance"
                                               v-if="ticket.draft_ticket && !ticket.draft_ticket.completed_at">{{ draftTicketLabel(ticket.draft_ticket) }}
                                        </badge>
                                        <div class="text-yellow-600 font-medium line-clamp-1" :title="ticket.latest_user_activity.notes"
                                             v-else-if="ticket.latest_user_activity && ticket.latest_user_activity.activity">{{
                                                ticket.latest_user_activity.activity
                                            }}
                                        </div>

                                        <badge :class="[getTransactionRequestStatusColor(ticket.latest_transaction_request.status), 'mt-1 flex flex-col']"
                                               text-size="text-sm" v-if="ticket.latest_transaction_request">
                                            <span class="w-full items-start">Billing Request {{ ticket.latest_transaction_request.status }}</span>
                                            <span v-if="ticket.latest_transaction_request.status === 'rejected'">
                                                {{ ticket.latest_transaction_request.rejection_message }}
                                            </span>
                                        </badge>
                                    </dd>
                                </dl>
                            </td>

                            <!-- DESKTOP STYLING -->
                            <td class="hidden lg:table-cell px-3 py-4 text-sm text-gray-900 font-medium lg:flex max-w-sm cursor-pointer"
                                @click.prevent="showTicketSummary(ticket)">
                                <div v-if="!ticket.resolved_at" class="block relative" :title="'Logged @ ' + ticket.logged_at">
                                    Logged {{ ticket.logged_at_relative }}
                                </div>

                                <div v-else class="block relative text-green-600" :title="'Resolved @ ' + ticket.resolved_at">
                                    Resolved {{ ticket.resolved_at_relative }}
                                </div>

                                <div class="mt-1 block flex items-center font-medium text-sm text-indigo-600">
                                    <badge class="text-yellow-600 bg-yellow-100" text-size="text-sm" v-if="ticket.phase">{{ ticket.phase.name }}</badge>
                                </div>
                            </td>

                            <td class="hidden px-3 py-4 text-sm text-gray-900 font-medium lg:table-cell cursor-pointer"
                                @click.prevent="showTicketSummary(ticket)">
                                <div class="block flex items-center" v-if="ticket.service_provider"
                                     :title="getContactDetailString(ticket.primary_service_provider, 'service provider')">
                                    {{ ticket.service_provider.name }}<span class="mx-1">&middot;</span>
                                    <span class="text-indigo-600 whitespace-nowrap">
                                        {{ ticket.service_provider.type }}
                                    </span>
                                </div>

                                <div v-else class="text-gray-400 font-normal">No Service Provider Assigned Yet</div>

                                <div class="block text-gray-500 font-normal text-sm line-clamp-1 max-w-xl mt-1">{{ ticket.description }}</div>

                                <badge class="text-red-500 bg-red-100" text-size="text-sm"
                                       v-if="ticket.draft_ticket && !ticket.draft_ticket.completed_at">{{ draftTicketLabel(ticket.draft_ticket) }}
                                </badge>
                                <div class="block text-yellow-600 font-medium text-sm line-clamp-1 max-w-xl mt-1"
                                     :title="ticket.latest_user_activity.notes"
                                     v-else-if="ticket.latest_user_activity && ticket.latest_user_activity.activity">
                                    {{ ticket.latest_user_activity.activity }}
                                </div>

                                <badge :class="[getTransactionRequestStatusColor(ticket.latest_transaction_request.status), 'mt-1 flex flex-col']"
                                       v-if="ticket.latest_transaction_request">
                                    <span class="w-full items-start">Billing Request {{ ticket.latest_transaction_request.status }}</span>
                                    <span v-if="ticket.latest_transaction_request.status === 'rejected'">
                                        {{ ticket.latest_transaction_request.rejection_message }}
                                    </span>
                                </badge>
                            </td>

                            <td class="hidden lg:table-cell px-3 py-4 text-sm">
                                <div v-if="ticket.assigned_to" class="text-indigo-600 font-medium">{{ ticket.assigned_to.name }}</div>

                                <div v-if="!ticket.next_todo" class="text-gray-400 font-normal whitespace-nowrap">No Reminder Set</div>
                                <div v-else class="" @click="editTodo(ticket.next_todo, ticket)">
                                    <div class="block font-medium cursor-pointer"
                                         :class="reminderIsDueClass(ticket.next_todo.latest_reminder.reminder_date) +
                                         ' hover:' +reminderIsDueClass(ticket.next_todo.latest_reminder.reminder_date,true)">
                                        {{ ticket.next_todo.latest_reminder.reminder_date | date }}
                                    </div>
                                </div>
                            </td>

                            <td class="hidden sm:table-cell py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                <a :href="`/tickets/${ticket.id}`" class="font-medium text-indigo-600 hover:text-indigo-500">View</a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <simple-pagination
            v-if="paginationData && paginationData.total > 0"
            :meta="paginationData"
            per-page="10"
            :current-page="paginationData.current_page"
            padding="6"
            @page-changed="fetchTickets">
        </simple-pagination>

        <ticket-create-modal-base
            :show="showCreateTicketModal"
            @close-modal="showCreateTicketModal = false">
        </ticket-create-modal-base>

        <ticket-summary-modal
            :selected-ticket="selectedTicket"
            :show-ticket-summary-modal="showTicketSummaryModal"
            @close="showTicketSummaryModal = false">
        </ticket-summary-modal>

        <simple-modal :show="showTodoModal" @close="toggleTodoModal()"
                      title="Edit To-do">
            <todo-create-edit-form @close="toggleTodoModal" :todo="selectedTodo" @todo-updated="fetchTickets"></todo-create-edit-form>
        </simple-modal>
    </div>
</template>

<script>
import GroupToggleSelect from "../library/GroupToggleSelect.vue";
import { upperFirst } from "lodash/string";
import { DATE_RANGE_SHORTCUTS_FULL } from "../../mixins/DateRangeShortcuts";
import ExportFileMixin from "../../mixins/export-file";
import TicketStyles from "../../mixins/ticket-styles";
import TicketSummaryModal from "./TicketSummaryModal.vue";

export default {
    name: "TicketsList",

    components: {TicketSummaryModal, GroupToggleSelect},

    mixins: [ExportFileMixin, TicketStyles],

    props: {
        users: {
            type: Array,
            default: () => [],
            description: "Team users array"
        },
        phases: {
            type: Array,
            default: () => [],
            description: "Team ticket phases array"
        },
        transactionRequestStatusOptions: {
            type: Array,
            default: () => ["new", "billed", "rejected"]
        },
        priorityOptions: {
            type: Array,
            default: () => ["low", "medium", "high"]
        },
    },

    data () {
        return {
            activeFiltersCount: 0,

            ticketListLoading: true,
            mutableTicketList: [],
            serviceProviderTypes: [],
            paginationData: {},

            selectedTicket: null,

            datePickerOptions: {
                shortcuts: DATE_RANGE_SHORTCUTS_FULL
            },
            searchParams: {
                archived_filter: false,
                filter_text: "",
                filter_reference: "",
                logged_at_date_range_filter: null,
                phases_filter: [],
                priorities_filter: [],
                resolution_filter: null,
                service_provider_types: [],
                status_filter: "open",
                transaction_request_statuses: [],
                user_filter: null,
                sort: {},
            },

            showCreateTicketModal: false,
            showTicketSummaryModal: false,
            showTodoModal: false,
            selectedTodo: null,
        };
    },

    computed: {
        userDropdownLabel () {
            return this.searchParams ?
                this.users.find(user => user.id == this.searchParams.user_filter) :
                null;
        },

        statusFilterLabel () {
            switch (this.searchParams?.status_filter) {
                case "open":
                    return "Open Tickets";
                case "closed":
                    return "Closed Tickets";
                default:
                    return "Open & Closed Tickets";
            }
        },

        selectedFiltersCount () {
            let count = 0;
            if (Object.keys(this.searchParams).length > 0) {
                if (this.searchParams.filter_text) {count++;}
                if (this.searchParams.filter_reference) {count++;}
                if ((this.searchParams.logged_at_date_range_filter && this.searchParams.logged_at_date_range_filter.length !== 0)) {count++;}
                if (this.searchParams.phases_filter.length !== 0) {count++;}
                if (this.searchParams.priorities_filter.length !== 0) {count++;}
                if (this.searchParams.resolution_filter) {count++;}
                if (this.searchParams.service_provider_types.length !== 0) {count++;}
                if (this.searchParams.status_filter !== "open") {count++;}
                if (this.searchParams.transaction_request_statuses.length !== 0) {count++;}
                if (this.searchParams.user_filter) {count++;}
            }
            return count;
        },
    },

    created () {
        this.setPreselectedFiltersFromUrl();
        this.fetchTickets();

        let self = this;
        Bus.$on("ticket-created", function () {
            self.fetchTickets();
        });
    },

    methods: {
        upperFirst,

        getContactDetailString (contact, role) {
            if (!contact) {
                return `No ${role} details`;
            }

            return [
                contact.contact_name ? contact.contact_name : null,
                contact.email ? contact.email : null,
                contact.mobile ? contact.mobile : null
            ].filter((val) => val != null).join(" / ");
        },

        setPreselectedFiltersFromUrl () {
            let url = new URL(window.location.href);

            let preselectedStatusFilter = url.searchParams.get("status_filter");
            if (preselectedStatusFilter === "all") {
                this.$set(this.searchParams, "status_filter", undefined);
            } else if (preselectedStatusFilter) {
                this.$set(this.searchParams, "status_filter", preselectedStatusFilter);
            }

            let preselectedResolutionFilter = url.searchParams.get("resolution_filter");
            if (preselectedResolutionFilter) {
                this.$set(this.searchParams, "resolution_filter", preselectedResolutionFilter);
            }

            let preselectedUserFilter = url.searchParams.get("user_filter");
            if (preselectedUserFilter) {
                this.$set(this.searchParams, "user_filter", _.toNumber(preselectedUserFilter));
            }
        },

        openLink (url, newTab) {
            newTab ? window.open(url, "_blank") : window.location.href = url;
        },

        showTicketSummary (ticket) {
            this.selectedTicket = ticket;
            this.showTicketSummaryModal = true;
        },

        closeTicketSummary () {
            this.showTicketSummaryModal = false;
            this.selectedTicket = null;
        },

        toggleTodoModal () {
            this.showTodoModal = !this.showTodoModal;
            if (!this.showTodoModal) {
                this.selectedTodo = null;
            }
        },

        editTodo (todo, ticket) {
            todo.ticket = ticket;
            Object.assign(todo.asset = {}, {id: ticket.asset_id, name: ticket.asset_name, portfolio_id: ticket.portfolio_id});
            this.selectedTodo = todo;
            this.toggleTodoModal();
        },

        fetchTickets: _.debounce(function (paginationSettings = null) {
            this.setPaginationAndSort(paginationSettings);
            this.ticketListLoading = true;

            axios.get("/tickets/all", {
                params: this.searchParams
            }).then((response) => {
                const {data, ...paginationData} = response.data;
                this.mutableTicketList = data;
                this.paginationData = paginationData.meta;
                this.activeFiltersCount = this.selectedFiltersCount;
                this.ticketListLoading = false;
            }).catch((error) => {
                toastr.error("Could not fetch tickets: " + error);
                this.ticketListLoading = false;
            });
        }, 800),

        clearFilters () {
            // purposefully don't clear the archived filter here
            this.$set(this.searchParams, "filter_text", null);
            this.$set(this.searchParams, "filter_reference", null);
            this.$set(this.searchParams, "logged_at_date_range_filter", null);
            this.$set(this.searchParams, "phases_filter", []);
            this.$set(this.searchParams, "priorities_filter", []);
            this.$set(this.searchParams, "resolution_filter", null);
            this.$set(this.searchParams, "service_provider_types", []);
            this.$set(this.searchParams, "status_filter", "open");
            this.$set(this.searchParams, "transaction_request_statuses", []);
            this.$set(this.searchParams, "user_filter", null);
            this.fetchTickets();
        },

        updateSorting (sortParam, direction) {
            if (sortParam === "logged_at") {
                this.searchParams.sort.reminder_date = "default";
            }

            if (sortParam === "reminder_date") {
                this.searchParams.sort.logged_at = "default";
            }

            this.searchParams.sort[sortParam] = direction;
            this.fetchTickets();
        },

        showTicketCreateForm () {
            this.showCreateTicketModal = true;
        },

        toggleShowArchivedTickets () {
            this.$set(this.searchParams, "archived_filter", !this.searchParams.archived_filter);
            this.fetchTickets();
        },

        setPaginationAndSort (paginationSettings = null) {
            this.$set(this.searchParams, "sort", {
                logged_at: this.searchParams?.sort?.logged_at ? this.searchParams.sort.logged_at : "desc",
                reminder_date: this.searchParams?.sort?.reminder_date ? this.searchParams.sort.reminder_date : "default",
            });

            this.$set(this.searchParams, "page", paginationSettings?.pageNumber ? paginationSettings.pageNumber : 1);
            this.$set(this.searchParams, "per_page", paginationSettings?.perPage ? paginationSettings.perPage : 10);
        },

        exportTickets () {
            this.setPaginationAndSort();
            this.exportData("/tickets/export", this.searchParams, this.$dayjs().format("DD-MM-YYYY") + " Tickets Export.xlsx");
        },

        loadServiceProviderTypes () {
            if (this.serviceProviderTypes.length === 0) {
                axios.get("/service-provider-types/all")
                .then((response) => {
                    this.serviceProviderTypes = response.data;
                }).catch((error) => {
                    toastr.error("Could not load service provider types. " + error.message);
                });
            }
        },

        reminderIsDueClass (reminderDate, hover = false) {
            let reminderDateEnd = this.$dayjs(reminderDate).endOf("d");
            let endOfDay = this.$dayjs().endOf("d");
            // True for overdue, false for upcoming, null for today
            if (reminderDateEnd.isBefore(endOfDay))
                return "text-red-" + (hover ? "800" : "600");
            else if (reminderDateEnd.isAfter(endOfDay))
                return "text-gray-" + (hover ? "600" : "400");
            else
                return "text-yellow-" + (hover ? "600" : "400");
        },

        draftTicketLabel (draftTicket) {
            let label = "";

            if (!draftTicket) {
                return label;
            }

            // when draft ticket hasn't been processed within an hour
            if (!draftTicket.completed_at && this.$dayjs().diff(draftTicket.created_at, "h") >= 1) {
                label = "Possible Creation Error";
            } else {
                label = "Creating Ticket";
            }

            return label;
        },
    },
};
</script>

<style scoped>
.el-tooltip__popper {
    max-width: 45vw !important;
}
</style>
